import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CheckCircle, Clock, Lock, Send, Sparkles, Users } from "lucide-react";

const CLUBS = [
  { key: "Diplomatas", emoji: "🤝", color: "#22C55E" },
  { key: "Analistas", emoji: "🧠", color: "#A855F7" },
  { key: "Sentinelas", emoji: "🛡️", color: "#3B82F6" },
  { key: "Exploradores", emoji: "🚀", color: "#EAB308" },
];

const STATUS_META = {
  pending: { label: "Em análise", color: "#EAB308", icon: Clock },
  accepted: { label: "Aceite", color: "#22C55E", icon: CheckCircle },
  rejected: { label: "Não selecionado", color: "#737373", icon: Lock },
};

export default function Apply() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    club: "", motivation: "", discovery_goal: "",
    commitment_level: "full", commitment_notes: "",
    leadership_interest: "none", pledge: false,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/applications/status");
      setStatus(r.data);
      if (r.data.mine) {
        setForm({
          club: r.data.mine.club || "",
          motivation: r.data.mine.motivation || "",
          discovery_goal: r.data.mine.discovery_goal || "",
          commitment_level: r.data.mine.commitment_level || "full",
          commitment_notes: r.data.mine.commitment_notes || "",
          leadership_interest: r.data.mine.leadership_interest || "none",
          pledge: true,
        });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.club) return toast.error("Escolhe o teu Clube.");
    if (!form.pledge) return toast.error("Confirma o compromisso para submeter.");
    setSaving(true);
    try {
      await api.post("/applications", form);
      toast.success("Candidatura submetida!");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (!status) return <div className="kw-overline">A carregar…</div>;

  const mine = status.mine;
  const isLocked = mine && mine.status && mine.status !== "pending";
  const canEdit = status.is_open && !isLocked;

  return (
    <div className="max-w-3xl space-y-6" data-testid="apply-page">
      <div>
        <div className="kw-overline">Próxima Edição</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
          Candidatura <span className="text-[#E50000]">Growth League</span>
        </h1>
      </div>

      <div className="kw-card p-6 space-y-3 text-[#E5E5E5]">
        <p className="font-bold">Este não é apenas um formulário de inscrição. É o primeiro passo de um compromisso.</p>
        <p className="text-[#A3A3A3] text-sm leading-relaxed">
          O programa <b>Do Autoconhecimento ao Crescimento</b> é uma experiência de autoeducação que combina
          autoconhecimento, vínculos, experimentação, ação e reflexão. Cada Clube terá <b>{status.quota_active} concorrentes</b>.
        </p>
        <p className="text-[#737373] text-xs">Demora cerca de 3 minutos a preencher.</p>
        {status.deadline_iso && (
          <p className="text-xs text-[#EAB308]"><Clock className="inline w-3 h-3 mr-1" /> Prazo: {new Date(status.deadline_iso).toLocaleDateString("pt-PT")}</p>
        )}
      </div>

      {mine && (
        <MyStatusBanner mine={mine} />
      )}

      {!status.is_open && !mine && (
        <div className="kw-card p-6 border-[#737373]">
          <Lock className="w-5 h-5 text-[#737373] mb-2" />
          <h2 className="font-display font-black uppercase">Candidaturas fechadas</h2>
          <p className="text-[#A3A3A3] text-sm mt-2">O período de candidaturas está encerrado. Aguarda abertura pelo admin.</p>
        </div>
      )}

      {(status.is_open || (mine && mine.status === "pending")) && (
        <form onSubmit={submit} className="kw-card p-6 space-y-6" data-testid="apply-form">
          {/* Club */}
          <div>
            <label className="kw-overline block mb-2 text-xs">1. Qual é o teu Clube?</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {CLUBS.map((c) => (
                <button key={c.key} type="button" disabled={!canEdit}
                  onClick={() => setForm({ ...form, club: c.key })}
                  className={`p-3 border text-sm font-bold uppercase tracking-wider transition-colors ${form.club === c.key ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3] hover:border-[#555]"} disabled:opacity-50`}
                  style={form.club === c.key ? {} : { borderColor: c.color }}
                  data-testid={`apply-club-${c.key}`}>
                  <div className="text-2xl mb-1">{c.emoji}</div>
                  {c.key}
                </button>
              ))}
            </div>
          </div>

          <TextArea label="2. Porque gostarias de participar nesta experiência? (2–3 linhas)"
            value={form.motivation}
            onChange={(v) => setForm({ ...form, motivation: v })}
            disabled={!canEdit}
            testid="apply-motivation" />

          <TextArea label="3. O que gostarias de descobrir, desenvolver ou transformar em ti ao longo deste percurso?"
            value={form.discovery_goal}
            onChange={(v) => setForm({ ...form, discovery_goal: v })}
            disabled={!canEdit}
            testid="apply-discovery" />

          <div>
            <label className="kw-overline block mb-2 text-xs">4. Estás disponível para te comprometer com as 8 sessões e desafios entre sessões?</label>
            <div className="space-y-2">
              {[
                { v: "full", t: "Sim, assumo esse compromisso." },
                { v: "with_limitations", t: "Tenho algumas limitações que gostaria de explicar." },
              ].map((o) => (
                <label key={o.v} className={`flex items-center gap-2 p-3 border cursor-pointer ${form.commitment_level === o.v ? "border-[#E50000] bg-[#E50000]/5" : "border-[#333]"} ${!canEdit ? "opacity-60" : ""}`}>
                  <input type="radio" name="commitment" disabled={!canEdit}
                    checked={form.commitment_level === o.v}
                    onChange={() => setForm({ ...form, commitment_level: o.v })}
                    className="accent-[#E50000]"
                    data-testid={`apply-commit-${o.v}`} />
                  <span className="text-sm">{o.t}</span>
                </label>
              ))}
            </div>
            {form.commitment_level === "with_limitations" && (
              <textarea className="kw-input !py-2 mt-2 min-h-[80px]"
                placeholder="Descreve as tuas limitações…"
                value={form.commitment_notes}
                disabled={!canEdit}
                onChange={(e) => setForm({ ...form, commitment_notes: e.target.value })}
                data-testid="apply-commit-notes" />
            )}
          </div>

          <div>
            <label className="kw-overline block mb-2 text-xs">5. Se não fores selecionado para coopetir, gostarias de assumir um cargo no teu clube?</label>
            <p className="text-xs text-[#737373] mb-3 leading-relaxed">
              O Secretário do Clube acompanha o plantel de um clube de perfil diferente do teu, para trazer um olhar
              de fora; o Presidente do Clube é do perfil do próprio clube e zela pela união e pela liderança do clube.
              Nenhum deles coopete — a escolha final é do plantel.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "none", t: "Não, obrigado" },
                { v: "coach", t: "Secretário do Clube" },
                { v: "administrator", t: "Presidente do Clube" },
                { v: "both", t: "Qualquer um dos dois" },
              ].map((o) => (                <button key={o.v} type="button" disabled={!canEdit}
                  onClick={() => setForm({ ...form, leadership_interest: o.v })}
                  className={`px-5 py-2 text-xs uppercase tracking-widest font-bold border ${form.leadership_interest === o.v ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"} disabled:opacity-50`}
                  data-testid={`apply-leadership-${o.v}`}>
                  {o.t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-start gap-3 p-4 border border-[#E50000]/40 bg-[#E50000]/5 cursor-pointer">
              <input type="checkbox" disabled={!canEdit}
                checked={form.pledge}
                onChange={(e) => setForm({ ...form, pledge: e.target.checked })}
                className="accent-[#E50000] w-4 h-4 mt-0.5"
                data-testid="apply-pledge" />
              <span className="text-sm leading-relaxed">
                Compreendo que existem vagas limitadas e que a candidatura representa a minha intenção de
                <b> participar ativamente, experimentar, executar, refletir e contribuir para o crescimento do meu Clube</b>.
                <br /><span className="text-[#EAB308] font-bold">Sim, quero candidatar-me.</span>
              </span>
            </label>
          </div>

          <button type="submit" disabled={!canEdit || saving || !form.pledge}
            className="kw-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="apply-submit">
            <Send className="w-4 h-4" />
            {saving ? "A submeter…" : mine ? "Atualizar candidatura" : "Submeter candidatura"}
          </button>
        </form>
      )}
    </div>
  );
}

function TextArea({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="kw-overline block mb-2 text-xs">{label}</label>
      <textarea value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="kw-input !py-2 min-h-[100px] resize-none"
        maxLength={1000}
        data-testid={testid} />
      <div className="text-[10px] text-[#737373] mt-1">{(value || "").length} / 1000</div>
    </div>
  );
}

function MyStatusBanner({ mine }) {
  const s = STATUS_META[mine.status || "pending"];
  const Icon = s.icon;
  return (
    <div className="kw-card p-5 flex items-center gap-4" style={{ borderColor: s.color }} data-testid="apply-my-status">
      <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${s.color}20`, color: s.color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="kw-overline" style={{ color: s.color }}>O teu estado</div>
        <div className="font-display font-black text-xl uppercase">{s.label}</div>
        {mine.status === "pending" && (
          <div className="text-xs text-[#A3A3A3] mt-1">Podes editar as respostas até o admin fechar o período.</div>
        )}
        {mine.admin_notes && (
          <div className="text-xs text-[#A3A3A3] mt-2 border-l-2 pl-2" style={{ borderColor: s.color }}>
            Nota do admin: {mine.admin_notes}
          </div>
        )}
      </div>
    </div>
  );
}
